import { supabase } from '@/lib/supabaseGuard';

export function isPendingConnectionRequestStatus(status: unknown): boolean {
  return String(status ?? '').toLowerCase() === 'pending';
}

export function uniqueSortedRecipientIds(
  ids: Array<string | null | undefined>
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  out.sort();
  return out;
}

export function sameSortedIds(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((id, index) => id === b[index]);
}

export function mergeOutgoingPendingIds(args: {
  fetched: string[];
  markedDuringFetch: string[];
  firstDegreeUserIds?: string[];
}): string[] {
  const connected = new Set(args.firstDegreeUserIds || []);
  return uniqueSortedRecipientIds(
    [...args.fetched, ...args.markedDuringFetch].filter((id) => id && !connected.has(id))
  );
}

export type OutgoingPendingReaderResult = {
  data: string[] | null;
  error: { message: string; code?: string } | null;
};

/**
 * Viewer-scoped outgoing pending recipient IDs.
 * One batched read — not per card. Filters pending/PENDING in JS.
 */
export async function fetchOutgoingPendingRecipientIds(
  senderId: string
): Promise<OutgoingPendingReaderResult> {
  if (!senderId) {
    return { data: [], error: null };
  }

  const { data, error } = await supabase
    .from('connection_requests')
    .select('recipient_id, status')
    .eq('sender_id', senderId);

  if (error) {
    console.error('❌ fetchOutgoingPendingRecipientIds failed:', error);
    return {
      data: null,
      error: { message: error.message, code: error.code },
    };
  }

  const ids = uniqueSortedRecipientIds(
    (data || [])
      .filter((row: { status?: string }) => isPendingConnectionRequestStatus(row.status))
      .map((row: { recipient_id?: string }) => row.recipient_id)
  );

  return { data: ids, error: null };
}
